import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';

import {
  FeatureCommands,
  FeatureNames,
  IsNumberValueProperty,
  Feature,
  GatewayDevicesProperties,
  NormallyOpenedClosed,
  FhtValveProperties,
  FeaturePropertyKey,
  FloorHeatingDamageProtectionThresholdCommands,
  FhtValveCommands,
} from '@vi-iot/client';

import {
  GatewayDevice,
  InstallationFeaturesStore,
  isRemoteDeviceOfType,
  RemoteDeviceType,
} from '@store';

import { GTMService } from '../../../shared/services';
import { InstallationService } from '../../services/installation.service';

@Component({
  selector: 'app-floor-heating-thermostat-manager',
  templateUrl: './floor-heating-thermostat-manager.component.html',
  styleUrls: ['./floor-heating-thermostat-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FloorHeatingThermostatManagerComponent implements OnInit {
  readonly NormallyOpenedClosed = NormallyOpenedClosed;
  damageProtectionThreshold$: Observable<
    Feature<IsNumberValueProperty, FloorHeatingDamageProtectionThresholdCommands>
  >;
  gatewayDevices$: Observable<Feature<GatewayDevicesProperties, never>>;

  channelsValveOpenOrClosed: { [channelId: string]: string } = {};

  fhtDeviceId: string;

  constructor(
    private installationService: InstallationService,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.fhtDeviceId = this.installationService.selectedFhtDeviceId$.getValue();
    this.damageProtectionThreshold$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      IsNumberValueProperty,
      FloorHeatingDamageProtectionThresholdCommands
    >(FeatureNames.fhtDamageProtectionThreshold, this.fhtDeviceId);

    this.gatewayDevices$ = this.installationFeaturesStore.selectInstallationGatewayFeature(
      FeatureNames.gatewayDevices
    );
  }

  assignValveValue(channelId: string, channelFeature: Feature<FhtValveProperties, any>): boolean {
    const value = channelFeature?.properties?.type?.value || '';
    if (value) {
      this.channelsValveOpenOrClosed[channelId] = value;

      return true;
    }

    return false;
  }

  onChannelValveChange(value: keyof typeof NormallyOpenedClosed, channelId: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.fhtChannelValve,
      value,
      FeaturePropertyKey.value,
      'type',
      FeatureCommands.setType,
      null,
      channelId
    );

    this.gtmService.track('onConfirm', 'Change floor heating channel valve type', 'fhtManager');
  }

  onCancelChannelValveChange(): void {
    this.gtmService.track('onCancel', 'Change floor heating channel valve type', 'fhtManager');
  }

  onDamageProtectionThresholdChange(newValue: number, feature: string): void {
    this.installationFeaturesStore.updateFeature(
      feature,
      newValue,
      FeaturePropertyKey.value,
      'value',
      FeatureCommands.setValue,
      null,
      this.fhtDeviceId
    );
    this.gtmService.track(
      'onSet',
      'Floor heating termostat damage protection treshold',
      'fhtManager'
    );
  }

  getFhtChannelsIds(devices: GatewayDevice[]): string[] {
    return devices
      .filter((device: GatewayDevice) =>
        isRemoteDeviceOfType(device.roles, RemoteDeviceType.FHT_CHANNEL)
      )
      .filter((device: GatewayDevice) => device.id.includes(this.fhtDeviceId))
      .map((device: GatewayDevice) => device.id);
  }

  getFhtId(fhtDeviceId: string): string {
    const splitedId = fhtDeviceId.split('-');

    return splitedId[0];
  }

  getFhtChannelValve(channelId: string): Observable<Feature<FhtValveProperties, FhtValveCommands>> {
    return this.installationFeaturesStore.selectInstallationDeviceFeature<
      FhtValveProperties,
      FhtValveCommands
    >(FeatureNames.fhtChannelValve, channelId);
  }

  trackClose(): void {
    this.gtmService.track('onClose', 'FHT Side panel', 'fhtManager');
  }
}
